
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumb from "../../components/ui/Breadcrumb";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import contractCategoryService from "./contractCategoryService";

const STORAGE_KEY = "ams_contract_categories";

const DEFAULT_CATEGORIES = [
  { id: 1, name: "Maintenance Agreement", code: "CAT-001", description: "Annual maintenance and servicing agreements", status: "Active" },
  { id: 2, name: "Warranty", code: "CAT-002", description: "Product and equipment warranty contracts", status: "Active" },
  { id: 3, name: "Software License", code: "CAT-003", description: "Software subscriptions and licensing", status: "Active" },
  { id: 4, name: "Service Agreement", code: "CAT-004", description: "Professional and technical service contracts", status: "Active" },
  { id: 5, name: "Lease Agreement", code: "CAT-005", description: "Equipment and property leasing contracts", status: "Inactive" },
];

const emptyForm = {
  name: "",
  code: "",
  description: "",
  status: "Active",
};

export default function ContractCategories() {
  const [collapsed, setCollapsed] = useState(false);
  const [categories, setCategories] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : DEFAULT_CATEGORIES;
    } catch {
      return DEFAULT_CATEGORIES;
    }
  });

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      const res = await contractCategoryService.getAll({ per_page: 200 });
      if (res.success && Array.isArray(res.data) && res.data.length > 0) {
        const normalized = res.data.map((item) => ({
          id: item.id,
          name: item.name,
          code: item.code,
          description: item.description || "",
          status: item.status && item.status.toLowerCase() === "active" ? "Active" : "Inactive",
        }));
        setCategories(normalized);
      }
    } catch (e) {
      console.warn("Could not load categories from API, using cached data:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(categories));
  }, [categories]);

  const filteredCategories = useMemo(() => {
    const query = search.trim().toLowerCase();

    return categories.filter((category) => {
      const matchesSearch =
        !query ||
        category.name.toLowerCase().includes(query) ||
        category.code.toLowerCase().includes(query) ||
        category.description.toLowerCase().includes(query);

      const matchesStatus =
        statusFilter === "All" || category.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [categories, search, statusFilter]);

  const activeCount = categories.filter((item) => item.status === "Active").length;
  const inactiveCount = categories.filter((item) => item.status === "Inactive").length;

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm, code: `CAT-${String(Date.now()).slice(-6)}` });
    setError("");
    setModalOpen(true);
  };

  const openEdit = (category) => {
    setEditingId(category.id);
    setForm({
      name: category.name,
      code: category.code,
      description: category.description,
      status: category.status,
    });
    setError("");
    setModalOpen(true);
  };

  const saveCategory = async (event) => {
    event.preventDefault();

    const name = form.name.trim();
    const code = form.code.trim().toUpperCase();

    if (!name || !code) {
      setError("Category name and category code are required.");
      return;
    }

    const duplicate = categories.some(
      (item) =>
        item.id !== editingId &&
        (item.name.toLowerCase() === name.toLowerCase() ||
          item.code.toLowerCase() === code.toLowerCase())
    );

    if (duplicate) {
      setError("A category with this name or code already exists.");
      return;
    }

    const record = {
      id: editingId ?? Date.now(),
      name,
      code,
      description: form.description.trim(),
      status: form.status,
    };

    if (editingId !== null) {
      setCategories((previous) =>
        previous.map((item) => (item.id === editingId ? record : item))
      );
    } else {
      setCategories((previous) => [record, ...previous]);
    }

    setModalOpen(false);
    setForm(emptyForm);
    setError("");

    try {
      const payload = {
        name,
        code,
        description: record.description,
        status: record.status.toLowerCase(),
      };
      if (editingId !== null) {
        await contractCategoryService.update(editingId, payload);
      } else {
        await contractCategoryService.create(payload);
      }
      loadCategories();
    } catch (apiErr) {
      console.warn("API update failed, local storage preserved:", apiErr);
    }
  };

  const deleteCategory = async () => {
    const idToDelete = deleteId;
    setCategories((previous) =>
      previous.filter((item) => item.id !== idToDelete)
    );
    setDeleteId(null);

    try {
      if (idToDelete) {
        await contractCategoryService.delete(idToDelete);
        loadCategories();
      }
    } catch (apiErr) {
      console.warn("API delete failed, local storage preserved:", apiErr);
    }
  };

  const exportCategories = () => {
    const columns = ["name", "code", "description", "status"];
    const escapeCSV = (value) =>
      `"${String(value ?? "").replace(/"/g, '""')}"`;

    const csv = [
      columns.join(","),
      ...filteredCategories.map((item) =>
        columns.map((column) => escapeCSV(item[column])).join(",")
      ),
    ].join("\n");

    const url = URL.createObjectURL(
      new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" })
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = "contract-categories.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="flex">
        <Sidebar
          isCollapsed={collapsed}
          onToggleCollapse={() => setCollapsed((value) => !value)}
        />

        <main
          className={`mt-16 min-w-0 flex-1 transition-all ${
            collapsed ? "ml-16" : "ml-72"
          }`}
        >
          <div className="p-5 md:p-8">
            <Breadcrumb />

            <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                  <Icon name="FolderOpen" size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground md:text-3xl">
                    Contract Categories
                  </h1>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Organize and manage contract classifications.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={exportCategories}>
                  <Icon name="Download" size={16} /> Export CSV
                </Button>
                <Button onClick={openAdd}>
                  <Icon name="Plus" size={16} /> Add Category
                </Button>
              </div>
            </div>

            <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[
                { label: "Total Categories", value: categories.length, icon: "Folders" },
                { label: "Active Categories", value: activeCount, icon: "CheckCircle" },
                { label: "Inactive Categories", value: inactiveCount, icon: "CircleOff" },
              ].map((stat) => (
                <div key={stat.label} className="rounded-xl border border-border bg-card p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{stat.label}</p>
                      <p className="mt-2 text-3xl font-bold text-foreground">{stat.value}</p>
                    </div>
                    <Icon name={stat.icon} size={23} className="text-primary" />
                  </div>
                </div>
              ))}
            </div>

            <section className="overflow-hidden rounded-xl border border-border bg-card">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-5">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Category Directory</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {filteredCategories.length} categories found
                  </p>
                </div>

                <div className="flex flex-wrap gap-3">
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search categories..."
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm sm:w-64"
                  />

                  <select
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                    className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  >
                    <option value="All">All statuses</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-muted/30">
                    <tr>
                      {["Category", "Code", "Description", "Status", "Actions"].map((heading) => (
                        <th key={heading} className="whitespace-nowrap px-5 py-4 text-xs font-semibold uppercase text-muted-foreground">
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCategories.map((category) => (
                      <tr key={category.id} className="border-t border-border hover:bg-muted/20">
                        <td className="whitespace-nowrap px-5 py-4 font-medium text-foreground">
                          {category.name}
                        </td>
                        <td className="whitespace-nowrap px-5 py-4 text-sm">{category.code}</td>
                        <td className="min-w-64 px-5 py-4 text-sm text-muted-foreground">
                          {category.description || "—"}
                        </td>
                        <td className="px-5 py-4">
                          <span className={`rounded-full px-3 py-1 text-xs font-medium ${
                            category.status === "Active"
                              ? "bg-success/10 text-success"
                              : "bg-muted text-muted-foreground"
                          }`}>
                            {category.status}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-5 py-4">
                          <button type="button" title="Edit" onClick={() => openEdit(category)} className="mr-3 text-primary">
                            <Icon name="Pencil" size={17} />
                          </button>
                          <button type="button" title="Delete" onClick={() => setDeleteId(category.id)} className="text-destructive">
                            <Icon name="Trash2" size={17} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!filteredCategories.length && (
                      <tr>
                        <td colSpan="5" className="px-5 py-12 text-center text-sm text-muted-foreground">
                          No categories found. Add a category or change your search.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </main>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <form onSubmit={saveCategory} className="w-full max-w-lg rounded-xl border border-border bg-card p-6 shadow-xl">
            <h2 className="text-xl font-semibold text-foreground">
              {editingId !== null ? "Edit Contract Category" : "Add Contract Category"}
            </h2>

            {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

            <div className="mt-5 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium">Category Name *</label>
                <input required maxLength={100} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2.5" placeholder="e.g. Maintenance Agreement" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Category Code *</label>
                <input required maxLength={30} value={form.code} onChange={(event) => setForm({ ...form, code: event.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2.5" placeholder="e.g. CAT-006" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Description</label>
                <textarea rows={3} maxLength={500} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2.5" placeholder="Describe this category..." />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Status</label>
                <select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2.5">
                  <option>Active</option>
                  <option>Inactive</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button type="submit">{editingId !== null ? "Save Changes" : "Create Category"}</Button>
            </div>
          </form>
        </div>
      )}

      {deleteId !== null && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl bg-card p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-foreground">Delete Category?</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              This category will be removed from this browser's saved data.
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
              <Button onClick={deleteCategory}>Delete</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
